import {Button, FormControl, FormHelperText, Grid, RadioGroup, Stack, Typography,} from '@mui/material'
import React, {useContext, useEffect} from 'react'
import {AppContext} from '../../../contexts/AppContextProvider'
import {CancelButton, PreviousButton} from '../../StepperButtons'
import {ComplaintTypeState, LocationOptions, ProductTypeOptions, YesNoOptions,} from '../../../contexts/types'
import OsseointegrationOptions from './OsseointegrationOptions'
import LabelingPackagingOptions from './LabelingPackagingOptions'
import FitOptions from './FitOptions'
import DamageFractureOptions from './DamageFractureOptions'
import EstheticOptions from './EstheticOptions'
import IFUOptions from './IFUOptions'
import SurfaceOptions from './SurfaceOptions'
import MRICompatibilityOptions from './MRICompatibility'
import OtherOptions from './OtherOptions'
import {Trans} from 'react-i18next'
import productTypeIsOneOf from '../../../lib/productTypeIsOneOf'
import {useForm} from '../../../hooks/useForm'
import isSameDay from 'date-fns/isSameDay'
import {OrgCustomizationContext} from "../../../themes/OrgCustomizationProvider";
import {setComplaintType} from "../../../contexts/ComplaintTypeReducer";
import useTheHackiestPageNavigation from "../../../hooks/useTheHackiestPageNavigation";
import {db} from "../../../lib/db";

const ComplaintType: React.FC = () => {
    const {navigateForward} = useTheHackiestPageNavigation()
    const {complaint, dispatch, postNotification, clearNotification} = useContext(AppContext)
    const {t, tns, orgIdentifier} = useContext(OrgCustomizationContext)

    const productType = complaint.generalComplaint
        .productType as ProductTypeOptions

    const shouldShowLackOfStabilityOption = isSameDay(
        complaint.complaintType.dateOfOccurrence as Date,
        complaint.treatmentDates.dateOfTreatment as Date
    )

    const required = {value: true, message: t('this_field_is_required')}
    const {
        data,
        errors,
        handleChange,
        validateFields,
        enableOnChangeValidation,
        setFormData
    } = useForm<ComplaintTypeState>({
        initialValues: {
            ...complaint.complaintType,
            complaintType:
                !shouldShowLackOfStabilityOption &&
                complaint.complaintType.complaintType === 'LACK_STABILITY'
                    ? ''
                    : complaint.complaintType.complaintType,
        },
        validations: {
            complaintType: {required},
            estheticExplanation: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.complaintType === 'OTHER_OUTCOME' &&
                            !data.estheticExplanation
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
            otherExplanation: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.complaintType === 'OTHER_DEFICIENCIES' &&
                            !data.otherExplanation
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
            surfaceDefectExplanation: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.complaintType === 'SURFACE_DEFECT' &&
                            !data.surfaceDefectExplanation
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
            attachmentIds: {
                required: {
                    value: (value, data) => {
                        return !(['WRONG_PART_PACKAGED', 'DAMAGE_WITH_PACKAGING_INTACT'].includes(data.complaintType) && !data.attachmentIds?.length)
                    },
                    message: t('this_field_is_required')
                },
            }
        },
    })
    
    useEffect( () => {
        if (!['WRONG_PART_PACKAGED', 'DAMAGE_WITH_PACKAGING_INTACT'].includes(data.complaintType) && data.attachmentIds.length > 0) {
            db.attachments.bulkDelete(data.attachmentIds)
            setFormData({...data, attachmentIds: []})
        }
    }, [data.complaintType])

    const wasPatientInvolved = complaint.generalComplaint.wasPatientInvolved
    const eventOccurrence = complaint.complaintType.location

    const handleSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleChange('complaintType', e.target.value)
    }

    const handleTextFieldChange = (
        fieldName: keyof ComplaintTypeState,
        value: string
    ) => {
        handleChange(fieldName, value)
    }

    const handleSubmit = async () => {
        // Activate field level validation tracking
        enableOnChangeValidation(true)

        // Validate the form fields
        const isFormValid = validateFields(data)

        // Stop processing if the form has invalid fields
        if (!isFormValid) {
            postNotification('error', t('error_mess'))
            return
        }

        // Form is valid, proceed with processing

        // Close the alert message in case it is open
        clearNotification()

        // Package up the form data and update app state
        const complaintType: ComplaintTypeState = {
            ...data,
            estheticExplanation:
                data.complaintType === 'OTHER_OUTCOME'
                    ? data.estheticExplanation
                    : '',
            otherExplanation:
                data.complaintType === 'OTHER_DEFICIENCIES'
                    ? data.otherExplanation
                    : '',
            surfaceDefectExplanation:
                data.complaintType === 'SURFACE_DEFECT'
                    ? data.surfaceDefectExplanation
                    : '',
        }

        dispatch(setComplaintType(complaintType))

        navigateForward()
    }

    return (
        <Grid container>
            <Stack spacing={2} sx={{width: '100%'}}>
                <Typography variant='h5' sx={{mb: 4}}>
                    {t('cti_complaint_type')} -{' '}
                    {t(`pt_option_${productType.toLowerCase()}`).toLowerCase()}
                </Typography>

                <Typography variant='h6' color='secondary.contrastText'>
                    <Trans i18nKey={tns('cti_select_type_of_complaint')}>
                        -
                        <Typography
                            component='span'
                            sx={{color: 'primary.main', fontSize: 'inherit'}}
                        >
                            -
                        </Typography>
                        -
                    </Trans>
                    :
                </Typography>

                <FormControl sx={{pt: 2}}>
                    <RadioGroup
                        value={data.complaintType}
                        onChange={handleSelection}
                    >
                        {wasPatientInvolved === 'YES' &&
                        productTypeIsOneOf(['IMPLANT'], productType) &&
                        (eventOccurrence === '4_CLINIC_DURING_PROCEDURE' ||
                            eventOccurrence === '5_CLINIC_AFTER_PROCEDURE') ? (
                            <OsseointegrationOptions
                                productType={productType}
                                complaint={complaint}
                            />
                        ) : null}

                        {eventOccurrence === '1_LAB_INITIAL_INSPECTION' ||
                        eventOccurrence === '2_LAB_DURING_FINALIZATION' ||
                        eventOccurrence === '3_CLINIC_INITIAL_INSPECTION' ? (
                            <LabelingPackagingOptions
                                productType={productType}
                                selectedOption={data.complaintType}
                                complaint={complaint}
                                attachmentIds={data.attachmentIds}
                                setAttachmentIds={a => setFormData({...data, attachmentIds: a})}
                                attachmentsError={errors.attachmentIds}
                            />
                        ) : null}

                        <FitOptions
                            productType={productType}
                            complaint={complaint}
                        />

                        <DamageFractureOptions complaint={complaint}/>

                        {productTypeIsOneOf(['ABUTMENT'], productType) ||
                        (productTypeIsOneOf(['IMPLANT'], productType) &&
                            wasPatientInvolved === 'YES') ? (
                            <EstheticOptions
                                productType={productType}
                                selectedOption={data.complaintType}
                                handleTextFieldChange={handleTextFieldChange}
                                estheticExplanation={data.estheticExplanation}
                                estheticExplanationError={
                                    errors.estheticExplanation as string
                                }
                                complaint={complaint}
                            />
                        ) : null}

                        {eventOccurrence === '1_LAB_INITIAL_INSPECTION' ||
                        eventOccurrence === '2_LAB_DURING_FINALIZATION' ||
                        eventOccurrence === '3_CLINIC_INITIAL_INSPECTION' ||
                        eventOccurrence === '4_CLINIC_DURING_PROCEDURE' ? (
                            <IFUOptions complaint={complaint}/>
                        ) : null}

                        <SurfaceOptions
                            productType={productType}
                            selectedOption={data.complaintType}
                            handleTextFieldChange={handleTextFieldChange}
                            surfaceDefectExplanation={data.surfaceDefectExplanation}
                            surfaceDefectExplanationError={errors.surfaceDefectExplanation as string}

                        />

                        {wasPatientInvolved === 'YES' &&
                        productTypeIsOneOf(
                            ['IMPLANT', 'ABUTMENT', 'SCREW'],
                            productType
                        ) &&
                        (eventOccurrence === '4_CLINIC_DURING_PROCEDURE' ||
                            eventOccurrence === '5_CLINIC_AFTER_PROCEDURE') ? (
                            <MRICompatibilityOptions
                                productType={productType}
                            />
                        ) : null}

                        
                        <OtherOptions
                            selectedOption={data.complaintType}
                            handleTextFieldChange={handleTextFieldChange}
                            otherExplanation={data.otherExplanation}
                            otherExplanationError={
                                errors.otherExplanation as string
                            }
                            wasPatientInvolved={
                                complaint.generalComplaint
                                    .wasPatientInvolved as YesNoOptions
                            }
                            eventOccurrence={
                                complaint.complaintType
                                    .location as LocationOptions
                            }
                        />
                        
                    </RadioGroup>
                </FormControl>
                <FormHelperText sx={{pl: 2}} error={!!errors.complaintType}>
                    {errors.complaintType}
                </FormHelperText>
            </Stack>

            <Stack direction='row-reverse' spacing={2} sx={{pt: 4, width: '100%'}}>
                <Button
                    variant='contained'
                    color='primary'
                    onClick={() => handleSubmit()}
                >
                    {t('continue')}
                </Button>

                <PreviousButton before={() => dispatch(setComplaintType(data))}/>

                <CancelButton/>
            </Stack>
        </Grid>
    )
}

export default ComplaintType
